import type { ReactNode } from 'react';
import { SiteLink as Link } from '../SiteLink';
import { ArrowRight, Check, Minus } from '@phosphor-icons/react';
import { Painting } from '../Painting';
import type { PaintingName } from '../Painting';
import { linkProps } from '../../lib/links';

/**
 * The interior page template. Every page other than the landing page is built
 * from these pieces, so content can be loaded into it and still match the F3
 * design (DESIGN.md): cream ground, Satoshi, one clay accent, paintings as the
 * only colour, hairline rows rather than card grids.
 *
 * Styles: .pg-* in src/styles/statio.css. Pages must not use Tamagui layout
 * primitives or inline colour; if a page needs a new pattern, add it here.
 */

/** A link that knows whether it is a route, an anchor or off-site. */
export function SmartLink({
  href,
  children,
  className = 's-link',
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  const p = linkProps(href);
  if (p.to)
    return (
      <Link className={className} to={p.to}>
        {children}
      </Link>
    );
  return (
    <a
      className={className}
      href={p.href}
      target={p.external ? '_blank' : undefined}
      rel={p.external ? 'noopener noreferrer' : undefined}
    >
      {children}
    </a>
  );
}

export function MoreLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <SmartLink href={href}>
      {children} <ArrowRight size={16} weight="bold" />
    </SmartLink>
  );
}

/** Page header. One h1 per page. `art` adds a painting banner below it. */
export function PageHero({
  title,
  lede,
  actions,
  art,
  children,
}: {
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
  art?: PaintingName;
  children?: ReactNode;
}) {
  return (
    <header className="pg-hero">
      <div className="pg-hero-text">
        {/* The landing hero's entrance (DESIGN.md), staggered .08s. CSS-only, so the prerendered HTML is never blank. */}
        <h1 className="l-rise">{title}</h1>
        {lede && (
          <p className="pg-lede l-rise" style={{ animationDelay: '.08s' }}>
            {lede}
          </p>
        )}
        {actions && (
          <div className="pg-actions l-rise" style={{ animationDelay: '.16s' }}>
            {actions}
          </div>
        )}
        {children}
      </div>
      {art && <Painting name={art} className="pg-banner" />}
    </header>
  );
}

/**
 * A section with a top hairline. `split` puts the heading in a narrow left
 * column and the content on the right (the long-read layout); otherwise the
 * heading sits above the content.
 */
export function Section({
  id,
  title,
  lede,
  split = false,
  children,
}: {
  id?: string;
  title?: ReactNode;
  lede?: ReactNode;
  split?: boolean;
  children?: ReactNode;
}) {
  const head = (title || lede) && (
    <div className="pg-sec-head">
      {title && <h2 id={id ? `${id}-title` : undefined}>{title}</h2>}
      {lede && <p className="pg-sec-lede">{lede}</p>}
    </div>
  );
  return (
    <section
      id={id}
      className={split ? 'pg-sec split' : 'pg-sec'}
      aria-labelledby={id && title ? `${id}-title` : undefined}
    >
      {head}
      {children && <div className="pg-sec-body">{children}</div>}
    </section>
  );
}

export interface Row {
  title: ReactNode;
  body: ReactNode;
  href?: string;
  linkLabel?: string;
  meta?: ReactNode;
}

/** Titled rows separated by hairlines: the house replacement for icon-card grids. */
export function FeatureRows({ items, columns = 2 }: { items: Row[]; columns?: 1 | 2 }) {
  return (
    <ul className={columns === 1 ? 'pg-rows one' : 'pg-rows'}>
      {items.map((r, i) => (
        <li key={i}>
          {r.meta && <span className="pg-row-meta">{r.meta}</span>}
          <h3>{r.title}</h3>
          <div className="pg-row-body">{r.body}</div>
          {r.href && <MoreLink href={r.href}>{r.linkLabel || 'Learn more'}</MoreLink>}
        </li>
      ))}
    </ul>
  );
}

/** Numbered steps (clay numerals), for setup guides. */
export function Steps({ items }: { items: { title: ReactNode; body: ReactNode }[] }) {
  return (
    <ol className="pg-steps">
      {items.map((s, i) => (
        <li key={i}>
          <span className="pw-n" aria-hidden="true">
            {i + 1}
          </span>
          <div>
            <h3>{s.title}</h3>
            <div className="pg-row-body">{s.body}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Cards on the panel tone, for choices (use cases, plans, downloads). */
export function CardGrid({
  items,
}: {
  items: { title: ReactNode; body: ReactNode; href?: string; linkLabel?: string; extra?: ReactNode }[];
}) {
  return (
    <ul className="pg-cards">
      {items.map((c, i) => (
        <li key={i}>
          <h3>{c.title}</h3>
          <div className="pg-row-body">{c.body}</div>
          {c.extra}
          {c.href && <MoreLink href={c.href}>{c.linkLabel || 'Learn more'}</MoreLink>}
        </li>
      ))}
    </ul>
  );
}

/** Long-form text (legal, about). Style plain h2/h3/p/ul/ol children. */
export function Prose({ children }: { children: ReactNode }) {
  return <div className="pg-prose">{children}</div>;
}

/** An honest aside: what is not built yet, what is not certified, caveats. */
export function Note({ title, children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <aside className="pg-note">
      {title && <h3>{title}</h3>}
      <div>{children}</div>
    </aside>
  );
}

export type Cell = string | boolean | ReactNode;

/** Comparison table. `true`/`false` render as a check / a dash with sr text. */
export function DataTable({
  caption,
  columns,
  rows,
  highlight,
}: {
  caption: string;
  columns: string[];
  rows: Cell[][];
  highlight?: number;
}) {
  const cell = (c: Cell) =>
    c === true ? (
      <>
        <Check size={16} weight="bold" aria-hidden="true" className="pg-yes" />
        <span className="sr-only">Yes</span>
      </>
    ) : c === false ? (
      <>
        <Minus size={16} aria-hidden="true" className="pg-no" />
        <span className="sr-only">No</span>
      </>
    ) : (
      c
    );
  return (
    <div className="pg-table-wrap">
      <table className="pg-table">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={i} scope="col" className={i === highlight ? 'hi' : undefined}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) =>
                ci === 0 ? (
                  <th key={ci} scope="row">
                    {cell(c)}
                  </th>
                ) : (
                  <td key={ci} className={ci === highlight ? 'hi' : undefined}>
                    {cell(c)}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A painting band with a short statement and an action. */
export function PaintingBand({
  art,
  title,
  body,
  action,
}: {
  art: PaintingName;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="pg-band">
      <Painting name={art}>
        <div className="pg-band-text">
          <h2>{title}</h2>
          {body && <p>{body}</p>}
          {action}
        </div>
      </Painting>
    </section>
  );
}

/** Page body wrapper: width, gutters and vertical rhythm. */
export function PageBody({ children, narrow = false }: { children: ReactNode; narrow?: boolean }) {
  return <div className={narrow ? 's-wrap pg narrow' : 's-wrap pg'}>{children}</div>;
}

/** A product window (src/components/product) floating on a painting. */
export function PaintingWindow({ art, children }: { art: PaintingName; children: ReactNode }) {
  return (
    <Painting name={art} className="pg-pw">
      <div className="pg-pw-inner">{children}</div>
    </Painting>
  );
}
